import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, X, FileText, ChevronRight, Clock, FlaskConical } from 'lucide-react';
import { reportService } from '../../services/reportService';

// =============================================================================
// Reporting › one section — the tiled list of its reports.
//
// A FULL PAGE, NOT A MODAL. A report list is somewhere you arrive, linger, and
// come back to; it wants a URL you can send to someone and a back button that
// works. Everything here replaces the main pane rather than floating over it.
//
// `runnable` comes from the server and is honest about the gap between the
// catalogue and the code: a report row can exist before its dataset does, and
// a tile that looks live but errors on click is worse than one that says
// "not built yet" on its face.
// =============================================================================

export default function ReportSection() {
  const navigate = useNavigate();
  const { sectionCode } = useParams();

  const [data, setData] = useState({ section: null, reports: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getSection(sectionCode);
      setData(res.data || { section: null, reports: [] });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load this reporting section.');
    } finally {
      setLoading(false);
    }
  }, [sectionCode]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900 overflow-y-auto">
      <div className="px-6 pt-6 pb-4 shrink-0">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/reports')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Reporting
        </button>

        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {data.section?.section_name || sectionCode}
        </h1>
        {data.section?.description && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-3xl">{data.section.description}</p>
        )}

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      <div className="flex-1 px-6 pb-6">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : data.reports.length === 0 ? (
          <p className="m-0 py-10 text-sm text-slate-400 text-center">No reports in this section yet.</p>
        ) : (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(340px,1fr))]">
            {data.reports.map((r) => (
              <button
                key={r.report_code}
                onClick={() => r.runnable && navigate(`/reports/${sectionCode}/${r.report_code}`)}
                disabled={!r.runnable}
                className={`group text-left p-5 rounded-xl border transition-all ${r.runnable
                  ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 hover:shadow-md'
                  : 'border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 cursor-not-allowed'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${r.runnable
                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                    : 'bg-slate-100 dark:bg-slate-700/50 text-slate-400'}`}>
                    {r.runnable ? <FileText className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                  </span>
                  {r.runnable && (
                    <ChevronRight className="w-4 h-4 mt-3 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 transition-colors" />
                  )}
                </div>
                <h2 className={`mt-3 mb-0 text-base font-bold ${r.runnable ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
                  {r.report_name}
                </h2>
                <p className="mt-1 mb-0 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{r.description}</p>
                {!r.runnable && (
                  <span className="mt-3 inline-block text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    In the catalogue · not built yet
                  </span>
                )}
                {/* Three states, not two: not built, built on generated data,
                    built on the real thing. Collapsing the last two is how a
                    mock gets quoted in a meeting. */}
                {r.runnable && r.mock && (
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    <FlaskConical className="w-3 h-3" /> Illustrative layout · generated data
                  </span>
                )}
                {r.runnable && !r.mock && (
                  <span className="mt-3 inline-block text-[11px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    Live · your data
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
